
import React, { useEffect, useState } from 'react';
import { CFDI, FiscalpopCFDI, PagoCfdiPreXml, ToBillArguments, ToBillEvent } from '../../../models/Factura';
import { formatCurrencyStyled } from '../../visuals/currency.formatted';

import './MisFacturas.relationship.scss';

import moment from 'moment';
import { calculateTotal } from '../../../utils/calculate.conceptos';
import { getMontoFromPagoJson } from '../../../utils/pago.getter';
import { findCfdiByUuid } from '../../../services/fiscalpopApi';

moment.locale('es');

interface RelationshipProps {
    authToken: string;
    billArgument: ToBillArguments<ToBillEvent>;
    CFDI: CFDI;
    requestClose: () => void
}

// ─── Shared fetch hook ────────────────────────────────────────────────────────

function useFetchCFDI(uuid: string | undefined, authToken: string): FiscalpopCFDI | null {
    const [cfdi, setCfdi] = useState<FiscalpopCFDI>(null);

    useEffect(() => {
        if (!uuid) {
            setCfdi(null);
            return;
        }
        findCfdiByUuid(authToken, uuid)
            .then((b: FiscalpopCFDI[]) => setCfdi(b[0] || null))
            .catch(e => {
                console.error('Error looking for CFDI: ', e);
                setCfdi(null);
            });
    }, [uuid, authToken]);

    return cfdi;
}

// ─── Shared card component ────────────────────────────────────────────────────

interface RelationshipCardProps {
    uuidSelected: string;
    /** The uuid that identifies this card — drives _selected, _unstamped, and the displayed UUID row */
    selfUuid?: string;
    label: string;
    amount: number;
    /** Timestamp to format; null renders as "--" */
    date: number | null;
    serieFolio: string;
    isClosed?: boolean;
    /** 'none' suppresses the icon entirely (used for unstamped bill/nota entries) */
    iconStatus: 'check' | 'cancel' | 'none';
}

function RelationshipCard({ uuidSelected, selfUuid, label, amount, date, serieFolio, isClosed, iconStatus }: RelationshipCardProps) {
    return (
        <div className={[
            '_subRelationship',
            uuidSelected === selfUuid ? '_selected' : '',
            !selfUuid ? '_unstamped' : '',
            isClosed ? '_closed' : '',
        ].filter(Boolean).join(' ')}>
            {iconStatus === 'cancel' && <span className="material-icons _cancelled">close</span>}
            {iconStatus === 'check' && <span className="material-icons _checked">done</span>}
            <div className="row two gap-compressed">
                <p className="_bold">{label}</p>
                <p className="_bold end">{formatCurrencyStyled(amount)}</p>
            </div>
            <div className="row two gap-compressed">
                <p className="_info">{date ? moment(date).format('DD MMM YYYY') : '--'}</p>
                <p className="_info end">{serieFolio}</p>
            </div>
            {selfUuid
                ? <p className={`_info ${iconStatus === 'cancel' ? '_cancelled' : ''}`}>{selfUuid}</p>
                : ' '
            }
        </div>
    );
}

// ─── Child components ─────────────────────────────────────────────────────────

function RelationshipPaymentEvent({ event, uuidSelected, authToken }: { event: ToBillEvent; uuidSelected: string; authToken: string }) {
    const pago = useFetchCFDI(event.uuid, authToken);
    const dateRef = pago ? new Date(pago.json['@'].Fecha as string).getTime() : (event.closedAt || event.when);

    return (
        <RelationshipCard
            uuidSelected={uuidSelected}
            selfUuid={event.uuid}
            label={`Parcialidad ${event.paymentNum}`}
            amount={pago
                ? parseFloat(getMontoFromPagoJson(pago.json as PagoCfdiPreXml))
                : event.paymentCFDI.pagos[0].monto
            }
            date={dateRef}
            serieFolio={pago ? `${pago.json['@'].Serie} - ${pago.json['@'].Folio}` : ''}
            isClosed={event.isClosed}
            iconStatus={pago && !pago.status ? 'cancel' : 'check'}
        />
    );
}

function RelationshipBillEvent({ event, uuidSelected, authToken }: { event: ToBillEvent; uuidSelected: string; authToken: string }) {
    const bill = useFetchCFDI(event.uuid, authToken);
    const notaCredito = useFetchCFDI(event.notaUUID, authToken);

    const notaCard = event.NotaCreditoCFDI ? (
        <RelationshipCard
            uuidSelected={uuidSelected}
            selfUuid={notaCredito?.uuid}
            label="Nota de crédito"
            amount={notaCredito
                ? parseFloat(notaCredito.json['@'].Total as string)
                : calculateTotal(event.NotaCreditoCFDI.conceptos).total
            }
            date={notaCredito ? new Date(notaCredito.json['@'].Fecha as string).getTime() : null}
            serieFolio={notaCredito
                ? `${notaCredito.json['@'].Serie} - ${notaCredito.json['@'].Folio}`
                : `${event.NotaCreditoCFDI.serie} - ${event.NotaCreditoCFDI.folio}`
            }
            isClosed={event.isClosed}
            iconStatus={notaCredito ? (event.wasCanceled ? 'cancel' : 'check') : 'none'}
        />
    ) : null;

    const billCard = event.CFDI ? (
        <RelationshipCard
            uuidSelected={uuidSelected}
            selfUuid={bill?.uuid}
            label="Factura Liquidación"
            amount={bill
                ? parseFloat(bill.json['@'].Total as string)
                : calculateTotal(event.CFDI.conceptos).total
            }
            date={bill ? new Date(bill.json['@'].Fecha as string).getTime() : null}
            serieFolio={bill
                ? `${bill.json['@'].Serie} - ${bill.json['@'].Folio}`
                : `${event.CFDI.serie} - ${event.CFDI.folio}`
            }
            isClosed={event.isClosed}
            iconStatus={bill ? 'check' : 'none'}
        />
    ) : null;

    return (
        <>
            {notaCard}
            {billCard}
        </>
    );
}

// ─── Parent component ─────────────────────────────────────────────────────────

function MisFacturasRelationship({ billArgument, CFDI, authToken, requestClose }: RelationshipProps) {
    useEffect(() => {
        console.log('<MisFacturasRelationship> billArgument: ', billArgument);
        console.log('<MisFacturasRelationship> CFDI: ', CFDI);
    }, [billArgument, CFDI])

    const labelByType = (multipayType: number) => {
        if (multipayType === 0) { return 'Factura normal' }
        if (multipayType === 1) { return 'Factura adelanto' }
        if (multipayType === 2) { return 'Factura del total' }
        if (multipayType === 3) { return 'Factura del total' }
        return 'Factura del total'
    }

    if (!billArgument || !CFDI) {
        return (
            <div id="MisFacturasRelationship" className="card table">
            </div>
        )
    }

    const { total } = calculateTotal(billArgument.MasterCFDI.conceptos)

    return (
        <div id="MisFacturasRelationship" className="card table">
            <div className="cardTitle withClose">
                <h4>Relación de facturas</h4>
                <i className="material-icons" onClick={requestClose}>
                    close
                </i>
            </div>
            <div className="cardBody">
                <div className="relationship">
                    <div className={`_masterRelationship ${billArgument.uuid === CFDI.uuid ? '_selected' : ''}`}>
                        <div className="row two gap-compressed">
                            <p className={`_bold ${billArgument.wasCanceled ? '_canceled' : ''}`}> {labelByType(billArgument.multipayType)} </p>
                            <p className={`_bold end ${billArgument.wasCanceled ? '_canceled' : ''}`}> {formatCurrencyStyled(total)} </p>
                        </div>
                        <div className="row two gap-compressed">
                            <p className="_info"> {moment(billArgument.createdAt).format('DD MMM YYYY')} </p>
                            <p className="_info end"> {billArgument.MasterCFDI.serie} - {billArgument.MasterCFDI.folio}</p>
                        </div>
                        <p className="_info"> {billArgument.MasterCFDI.receptor.nombre} </p>
                        <p className={`_info ${billArgument.wasCanceled ? '_canceled' : ''}`}> {billArgument.uuid} </p>
                    </div>
                    {
                        billArgument.billEvents.map(bE => (
                            <RelationshipBillEvent authToken={authToken} event={bE} uuidSelected={CFDI.uuid} key={bE._id} />
                        ))
                    }
                    {
                        billArgument.paymentEvents.map(pE => (
                            <RelationshipPaymentEvent authToken={authToken} event={pE} uuidSelected={CFDI.uuid} key={pE._id} />
                        ))
                    }
                </div>
            </div>
        </div>
    )
}

export default MisFacturasRelationship;
